/**
 * Tutar formatlama yardımcıları.
 * Fatura/teklif/sipariş formlarındaki tutarlar binlik ayraçla (tr-TR) gösterilir,
 * ör. 1234567.89 -> "1.234.567,89". Böylece 100 bin ile 1 milyon ayırt edilebilir.
 * parseAmountInput ise kullanıcının her iki formatta da ("1.234,50", "1234.50",
 * "1234,5") yazabildiği girişleri güvenle sayıya çevirir.
 */

const trFormatter = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Sayıyı tr-TR binlik ayraçlı, 2 ondalıklı metne çevirir. */
export function formatAmount(value: number | null | undefined): string {
  const n = typeof value === 'number' && !isNaN(value) ? value : 0;
  return trFormatter.format(n);
}

/**
 * Kullanıcı girişini sayıya çevirir.
 * Kurallar:
 * - Hem nokta hem virgül varsa: sona yakın olan ondalık ayraçtır, diğeri binlik.
 * - Sadece virgül: tekse ondalık ("1234,5"), birden çoksa binlik ("1,234,567").
 * - Sadece nokta: tekse ondalık ("1234.5" - eski davranış korunur),
 *   birden çoksa binlik ("1.234.567").
 * Geçersiz girişte NaN döner.
 */
export function parseAmountInput(raw: string): number {
  const s = (raw ?? '').trim();
  if (!s) return NaN;

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  let normalized: string;

  if (lastComma >= 0 && lastDot >= 0) {
    normalized = lastComma > lastDot
      ? s.replace(/\./g, '').replace(',', '.')
      : s.replace(/,/g, '');
  } else if (lastComma >= 0) {
    normalized = s.split(',').length > 2 ? s.replace(/,/g, '') : s.replace(',', '.');
  } else if (lastDot >= 0) {
    normalized = s.split('.').length > 2 ? s.replace(/\./g, '') : s;
  } else {
    normalized = s;
  }

  return parseFloat(normalized.replace(/[^\d.-]/g, ''));
}

/**
 * Türkçe para girişini sayıya çevirir (kullanıcının yazdığı tutarlar için).
 * parseAmountInput'tan farkı: "1.500" binlik ayraçlı kabul edilir -> 1500 (1,5 değil).
 * Kurallar:
 * - Boşluk, "₺" ve "TL" atılır; başta "-" olabilir.
 * - Virgül varsa: noktalar binlik, virgül ondalıktır ("1.500,75" -> 1500.75).
 * - Virgül yoksa ve metin "1.500" / "1.234.567" kalıbındaysa noktalar binliktir.
 * - Aksi halde tek nokta ondalıktır ("12.5" -> 12.5).
 * - Sonuç 2 haneye yuvarlanır. Boş/geçersiz girişte NaN döner.
 */
export function parseTrAmount(raw: string): number {
  const s = (raw ?? '').replace(/\s/g, '').replace(/₺/g, '').replace(/TL/gi, '');
  if (!s) return NaN;

  let normalized: string;
  if (s.includes(',')) {
    // Birden fazla virgül geçersiz
    if (s.split(',').length > 2) return NaN;
    const [intPart, fracPart] = s.split(',');
    // Virgül varken noktalar yalnız tam sayı kısmında binlik ayraç olabilir
    if (fracPart.includes('.')) return NaN;
    normalized = `${intPart.replace(/\./g, '')}.${fracPart}`;
  } else if (/^-?[1-9]\d{0,2}(\.\d{3})+$/.test(s)) {
    // "1.500", "1.234.567": noktalar binlik ("0.500" gibi sıfırla başlayanlar ondalık sayılır)
    normalized = s.replace(/\./g, '');
  } else {
    normalized = s;
  }

  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(normalized)) return NaN;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return NaN;
  const rounded = Math.sign(n) * (Math.round((Math.abs(n) + Number.EPSILON) * 100) / 100);
  return rounded === 0 ? 0 : rounded;
}

/**
 * Türkçe miktar girişi (kg, lt...). Kurallar parseTrAmount ile aynıdır ("1.500" -> 1500,
 * "1,5" -> 1.5, "0.85" -> 0.85); fark: sonuç 2 değil `digits` haneye (varsayılan 6) yuvarlanır.
 * Boş/geçersiz girişte NaN döner.
 */
export function parseTrQuantity(raw: string, digits = 6): number {
  const s = (raw ?? '').replace(/\s/g, '');
  if (!s) return NaN;

  let normalized: string;
  if (s.includes(',')) {
    if (s.split(',').length > 2) return NaN;
    const [intPart, fracPart] = s.split(',');
    if (fracPart.includes('.')) return NaN;
    normalized = `${intPart.replace(/\./g, '')}.${fracPart}`;
  } else if (/^-?[1-9]\d{0,2}(\.\d{3})+$/.test(s)) {
    normalized = s.replace(/\./g, '');
  } else {
    normalized = s;
  }

  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(normalized)) return NaN;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return NaN;
  const scale = 10 ** digits;
  const rounded = Math.sign(n) * (Math.round((Math.abs(n) + Number.EPSILON) * scale) / scale);
  return rounded === 0 ? 0 : rounded;
}
